import { Hero } from "../../Components/Hero/Hero";
import Footer from "../../Components/Footer/Footer";
import siteConfig from "../../config/site";
import { Link } from "react-router-dom";

// ─── How It Works Section ──────────────────────────────────────
const HowItWorks = () => {
  const steps = [
    {
      number: "01",
      title: "Browse Entertainers",
      description: "Explore our curated roster of persona-driven performers. Each brings a unique energy and style.",
      icon: "👁️",
    },
    {
      number: "02",
      title: "Book Your Experience",
      description: "Choose your package, preferred performer, date and location. We handle the rest.",
      icon: "📋",
    },
    {
      number: "03",
      title: "Enjoy Your Private Show",
      description: "Sit back and experience a premium, private performance tailored to your occasion.",
      icon: "✨",
    },
  ];

  return (
    <section className="py-20 px-6 bg-[#0a0a0f]">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-4">
          How It Works
        </h2>
        <div className="w-20 h-0.5 bg-gradient-to-r from-purple-700 to-gold mx-auto mb-12" />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {steps.map((step) => (
            <div
              key={step.number}
              className="text-center p-8 rounded-2xl bg-gray-900/50 border border-purple-900/30 hover:border-purple-700/50 transition-all duration-300"
            >
              <div className="text-4xl mb-4">{step.icon}</div>
              <span className="text-gold font-bold text-sm tracking-widest">{step.number}</span>
              <h3 className="text-xl font-bold text-white mt-2 mb-3">{step.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── Services Overview ─────────────────────────────────────────
const ServicesOverview = () => {
  const { services } = siteConfig;
  // Show first 4 services on homepage
  const featured = services.slice(0, 4);

  return (
    <section className="py-20 px-6 bg-gradient-to-b from-[#0a0a0f] to-[#0d0d15]">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-4">
          Entertainment Services in Calgary
        </h2>
        <p className="text-gray-400 text-center max-w-2xl mx-auto mb-2">Available for outcall across Calgary</p>
        <div className="w-20 h-0.5 bg-gradient-to-r from-purple-700 to-gold mx-auto mb-12" />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {featured.map((service, idx) => (
            <div
              key={idx}
              className="p-6 rounded-2xl bg-gray-900/60 border border-purple-900/30 hover:border-gold/40 transition-all duration-300 group"
            >
              <div className="text-3xl mb-3">{service.icon}</div>
              <h3 className="text-lg font-bold text-white mb-2 group-hover:text-gold transition-colors">{service.name}</h3>
              <p className="text-gray-400 text-sm mb-4 leading-relaxed">{service.description}</p>
              <p className="text-gold font-semibold text-sm">{service.price}</p>
            </div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/services"
            className="inline-block px-8 py-3 border border-purple-700 text-purple-400 rounded-full hover:bg-purple-700 hover:text-white transition-all duration-300 font-medium"
          >
            View All Services
          </Link>
        </div>
      </div>
    </section>
  );
};

// ─── Meet Our Entertainers ─────────────────────────────────────
const MeetEntertainers = () => {
  const { entertainers } = siteConfig;

  return (
    <section className="py-20 px-6 bg-[#0a0a0f]">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-4">
          Meet Our Entertainers
        </h2>
        <div className="w-20 h-0.5 bg-gradient-to-r from-purple-700 to-gold mx-auto mb-12" />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {entertainers.map((performer) => (
            <div
              key={performer.id}
              className="rounded-2xl overflow-hidden bg-gray-900/60 border border-purple-900/30 hover:border-purple-600/50 transition-all duration-300 group"
            >
              {/* Placeholder image area */}
              <div className="h-64 bg-gradient-to-br from-purple-900/40 to-gray-900 flex items-center justify-center">
                <span className="text-6xl opacity-60">💎</span>
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-white mb-1">{performer.name}</h3>
                <p className="text-purple-400 text-sm mb-3 italic">{performer.tagline}</p>
                <p className="text-gray-400 text-sm leading-relaxed mb-4">
                  {performer.bio.length > 150 ? performer.bio.substring(0, 150) + "..." : performer.bio}
                </p>
                <Link
                  to="/book"
                  className="inline-block px-6 py-2 bg-gold text-black font-bold text-sm rounded-full hover:bg-gold-light transition-all duration-300"
                >
                  Book {performer.name}
                </Link>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/entertainers"
            className="inline-block px-8 py-3 border border-gold text-gold rounded-full hover:bg-gold hover:text-black transition-all duration-300 font-medium"
          >
            View All Entertainers
          </Link>
        </div>
      </div>
    </section>
  );
};

// ─── Why Exo Entertainment ─────────────────────────────────────
const WhyExoEntertainment = () => {
  const pillars = [
    {
      icon: "🔒",
      title: "Discreet",
      description: "Complete privacy guaranteed. What happens at your event stays at your event. No social media, no exposure.",
    },
    {
      icon: "👑",
      title: "Premium",
      description: "Every detail curated — from performer selection to the experience itself. This isn't budget entertainment.",
    },
    {
      icon: "🎭",
      title: "Persona-Driven",
      description: "Our performers craft distinct personas with depth, energy, and artistry. Every show tells a story.",
    },
    {
      icon: "🛡️",
      title: "Safe",
      description: "Strict safety protocols, consent frameworks, and professional boundaries. For performers and clients alike.",
    },
  ];

  return (
    <section className="py-20 px-6 bg-gradient-to-b from-[#0d0d15] to-[#0a0a0f]">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-4">
          Why Exo Entertainment
        </h2>
        <div className="w-20 h-0.5 bg-gradient-to-r from-purple-700 to-gold mx-auto mb-12" />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {pillars.map((pillar, idx) => (
            <div
              key={idx}
              className="p-8 rounded-2xl bg-gray-900/40 border border-gray-800/50 hover:border-gold/30 transition-all duration-300"
            >
              <div className="text-3xl mb-3">{pillar.icon}</div>
              <h3 className="text-xl font-bold text-white mb-2">{pillar.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{pillar.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── Calgary Location Section ──────────────────────────────────
const CalgarySection = () => {
  const highlights = [
    {
      icon: "🏙️",
      title: "Downtown & Beltline",
      description: "Hotels, condos, and private venues in the heart of Calgary. We come to you — discretion guaranteed.",
    },
    {
      icon: "🏨",
      title: "Hotel Outcalls",
      description: "We work with all major Calgary hotels. Book your room, book your show — we handle the rest.",
    },
    {
      icon: "🎉",
      title: "Private Residences",
      description: "Bachelor parties, birthdays, poker nights — anywhere in Calgary city limits. Your space, our show.",
    },
    {
      icon: "🕐",
      title: "Available 24/7",
      description: "Day or night, weekday or weekend — book anytime. We're always available for Calgary bookings.",
    },
  ];

  return (
    <section className="py-20 px-6 bg-[#0a0a0f]">
      <div className="max-w-6xl mx-auto">
        {/* Two-column hero: copy left, image right */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Copy */}
          <div>
            <p className="text-gold text-xs uppercase tracking-[0.2em] font-semibold mb-3">Serving Calgary & Area</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Private Entertainment in Calgary, AB
            </h2>
            <div className="w-20 h-0.5 bg-gradient-to-r from-purple-700 to-gold mb-6" />
            <p className="text-gray-400 leading-relaxed mb-4">
              Calgary's premier private entertainment service. Whether you're hosting a bachelor party in the Beltline, 
              a private event downtown, or a celebration anywhere in the city — Exo Entertainment delivers unforgettable, 
              curated experiences directly to your door.
            </p>
            <p className="text-gray-400 leading-relaxed mb-6">
              Our entertainers travel anywhere within Calgary city limits. Hotels, private residences, 
              event venues — your space, our show. Available 24/7, 365 days a year.
            </p>
            <Link
              to="/calgary"
              className="inline-block px-8 py-3 border border-gold text-gold rounded-full hover:bg-gold hover:text-black transition-all duration-300 font-medium text-sm uppercase tracking-wider"
            >
              View Calgary Services
            </Link>
          </div>

          {/* Placeholder image */}
          <div className="relative">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-gradient-to-br from-purple-900/40 via-gray-900 to-purple-950/30 border border-purple-900/20 flex items-center justify-center">
              {/* Replace with real Calgary/performer image */}
              <div className="text-center px-8">
                <div className="text-7xl mb-4 opacity-40">🏙️</div>
                <p className="text-gray-500 text-sm italic">Calgary performer photo coming soon</p>
              </div>
            </div>
            {/* Decorative accent */}
            <div className="absolute -bottom-3 -right-3 w-full h-full rounded-2xl border border-gold/20 -z-10" />
          </div>
        </div>

        {/* Location cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {highlights.map((item, idx) => (
            <div
              key={idx}
              className="p-6 rounded-2xl bg-gray-900/40 border border-gray-800/50 hover:border-gold/30 transition-all duration-300"
            >
              <div className="text-3xl mb-3">{item.icon}</div>
              <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── Testimonials Placeholder ──────────────────────────────────
const Testimonials = () => {
  const testimonials = [
    {
      text: "Absolutely professional from start to finish. The booking process was smooth and the performance was unforgettable.",
      author: "J.R.",
      event: "Bachelor Party",
    },
    {
      text: "Ember was incredible. The whole experience felt premium and tasteful — exactly what we were looking for.",
      author: "M.K.",
      event: "Private VIP Dance",
    },
    {
      text: "We hired Opal for a birthday party and she was amazing. High energy, professional, and everyone had a blast.",
      author: "S.T.",
      event: "Birthday Party",
    },
  ];

  return (
    <section className="py-20 px-6 bg-[#0a0a0f]">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-4">
          What Our Clients Say
        </h2>
        <div className="w-20 h-0.5 bg-gradient-to-r from-purple-700 to-gold mx-auto mb-12" />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((item, idx) => (
            <div
              key={idx}
              className="p-6 rounded-2xl bg-gray-900/40 border border-gray-800/30"
            >
              <div className="text-gold text-2xl mb-3">"</div>
              <p className="text-gray-300 text-sm leading-relaxed mb-4 italic">
                {item.text}
              </p>
              <div>
                <p className="text-white font-semibold text-sm">{item.author}</p>
                <p className="text-purple-400 text-xs">{item.event}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── Final CTA ─────────────────────────────────────────────────
const FinalCTA = () => (
  <section className="py-24 px-6 bg-gradient-to-b from-[#0a0a0f] via-purple-950/20 to-[#0a0a0f]">
    <div className="max-w-3xl mx-auto text-center">
      <h2 className="text-3xl sm:text-5xl font-bold text-white mb-4">
        Book Your Calgary Experience
      </h2>
      <p className="text-gray-400 text-lg mb-8 max-w-xl mx-auto">
        Calgary's premier private entertainment. Discreet, professional, unforgettable.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Link
          to="/book"
          className="px-10 py-4 bg-gold text-black font-bold text-lg rounded-full hover:bg-gold-light transition-all duration-300 shadow-lg shadow-gold/30"
        >
          Book Now
        </Link>
        <a
          href="mailto:booking@exoentertainment.ca"
          className="px-10 py-4 border border-purple-700 text-purple-400 font-bold text-lg rounded-full hover:bg-purple-700 hover:text-white transition-all duration-300"
        >
          Contact Us
        </a>
      </div>
      <p className="text-gray-500 text-sm mt-6">
        Available 24/7 · Serving all of Calgary, AB · booking@exoentertainment.ca
      </p>
    </div>
  </section>
);

// ─── Home Page ─────────────────────────────────────────────────
export const Home = () => {
  return (
    <div className="bg-[#0a0a0f]">
      <Hero handleClickedId={() => {}} />
      <HowItWorks />
      <ServicesOverview />
      <MeetEntertainers />
      <WhyExoEntertainment />
      <CalgarySection />
      <Testimonials />
      <FinalCTA />
      <Footer />
    </div>
  );
};

export default Home;
